import React from 'react';
import { Link } from 'react-router-dom';
import { usePageTitle } from '../hooks/usePageTitle';
import { APP_NAME } from '../config/branding';

const SmsConsent: React.FC = () => {
  usePageTitle('SMS Consent & Opt-In Policy');

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="mb-12 pb-6 border-b border-gray-300">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">SMS Consent & Opt-In Policy</h1>
          <p className="text-sm text-gray-600">
            How Upcactus, Inc. ({APP_NAME} d.b.a) collects and manages SMS messaging consent
          </p>
          <p className="text-sm text-gray-500 mt-2">
            Last updated: December 2024
          </p>
        </div>

        {/* Main Content */}
        <div className="prose prose-lg max-w-none space-y-8">
          
          {/* Overview Section */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Overview</h2>
            <p className="text-gray-700 leading-relaxed">
              Upcactus, Inc. ({APP_NAME} d.b.a) provides SMS messaging services to veterinary practices 
              for appointment reminders, confirmations, and other pet care communications. We are committed to 
              obtaining proper consent before sending any SMS messages and complying with all applicable regulations 
              including the Telephone Consumer Protection Act (TCPA) and carrier requirements.
            </p>
          </section>

          {/* How Consent is Collected */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">How Consent is Collected</h2>
            
            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">1. Pet Owner Registration</h3>
            <p className="text-gray-700 mb-3">
              When pet owners create an account or are registered by their veterinary practice, they 
              provide their mobile phone number and explicitly consent to receive SMS messages. The 
              registration form includes:
            </p>
            <ul className="list-disc pl-8 space-y-1 text-gray-700">
              <li>A clearly labeled phone number field</li>
              <li>An explicit checkbox to opt-in to SMS notifications</li>
              <li>Clear disclosure of message types (appointment reminders, confirmations, health alerts)</li>
              <li>Information about message frequency and how to opt-out</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">2. Appointment Booking</h3>
            <p className="text-gray-700 mb-3">
              When scheduling appointments through our online booking system or when a veterinary 
              practice schedules on behalf of the pet owner, consent is captured as part of the 
              booking flow:
            </p>
            <ul className="list-disc pl-8 space-y-1 text-gray-700">
              <li>Phone number is required for appointment confirmation</li>
              <li>Explicit consent checkbox for appointment-related SMS</li>
              <li>Clear language: "I agree to receive appointment reminders and updates via SMS"</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">3. Practice-Collected Consent</h3>
            <p className="text-gray-700 mb-3">
              Veterinary practices using {APP_NAME} may also collect consent directly from clients:
            </p>
            <ul className="list-disc pl-8 space-y-1 text-gray-700">
              <li>Paper intake forms with SMS opt-in checkbox</li>
              <li>Verbal consent documented in the practice management system</li>
              <li>Digital check-in kiosks with consent acknowledgment</li>
            </ul>
          </section>

          {/* Consent Language */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Consent Disclosure Language</h2>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
              <p className="text-gray-700 italic">
                "By providing your mobile phone number and checking this box, you consent to receive 
                automated SMS text messages from [Practice Name] via {APP_NAME} regarding your pet's 
                appointments, reminders, and veterinary care updates. Message frequency varies. 
                Message and data rates may apply. Reply STOP to unsubscribe at any time. Reply HELP 
                for assistance. See our <Link to="/privacy" className="text-blue-600 underline">Privacy Policy</Link> and <Link to="/terms" className="text-blue-600 underline">Terms of Service</Link> for more information."
              </p>
            </div>
          </section>

          {/* Types of Messages */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Types of Messages Sent</h2>
            <ul className="list-disc pl-8 space-y-2 text-gray-700">
              <li><strong>Appointment Reminders:</strong> Automated reminders 24-48 hours before scheduled appointments</li>
              <li><strong>Confirmations:</strong> Booking confirmations and appointment changes</li>
              <li><strong>Medication Reminders:</strong> Prescription refill and medication schedule alerts</li>
              <li><strong>Health Updates:</strong> Post-visit care instructions and lab results notifications</li>
            </ul>
          </section>

          {/* Opt-Out Process */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">How to Opt-Out</h2>
            <p className="text-gray-700 mb-3">
              Recipients can opt-out of SMS messages at any time using any of these methods:
            </p>
            <ul className="list-disc pl-8 space-y-2 text-gray-700">
              <li><strong>Reply STOP:</strong> Text "STOP" to any message to immediately unsubscribe</li>
              <li><strong>Account Settings:</strong> Toggle off SMS notifications in your {APP_NAME} account</li>
              <li><strong>Contact Practice:</strong> Request your veterinary practice to disable SMS notifications</li>
              <li><strong>Contact Support:</strong> Email <a href="mailto:support@helppet.ai" className="text-blue-600 underline">support@helppet.ai</a> to opt-out</li>
            </ul>
            <p className="text-gray-700 mt-4">
              Once opted out, you will receive a confirmation message and no further SMS messages will be sent 
              unless you re-subscribe.
            </p>
          </section>

          {/* Data Protection */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Data Protection & Privacy</h2>
            <p className="text-gray-700 mb-3">
              Your phone number and consent records are securely stored and protected. We maintain 
              records of:
            </p>
            <ul className="list-disc pl-8 space-y-1 text-gray-700">
              <li>Date and time consent was provided</li>
              <li>Method of consent collection (online form, booking system, etc.)</li>
              <li>IP address and device information at time of consent</li>
              <li>The specific consent language presented</li>
              <li>Any opt-out requests and when they were processed</li>
            </ul>
            <p className="text-gray-700 mt-4">
              For more details, please review our <Link to="/privacy" className="text-blue-600 underline">Privacy Policy</Link>.
            </p>
          </section>

          {/* Contact */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Questions?</h2>
            <p className="text-gray-700 mb-4">
              If you have questions about our SMS consent practices or need to update your preferences, 
              please contact us:
            </p>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
              <p className="text-gray-700"><strong>Company:</strong> Upcactus, Inc. ({APP_NAME} d.b.a)</p>
              <p className="text-gray-700 mt-2"><strong>Email:</strong> <a href="mailto:support@helppet.ai" className="text-blue-600">support@helppet.ai</a></p>
              <p className="text-gray-700 mt-2"><strong>Website:</strong> <a href="https://helppet.ai" className="text-blue-600">helppet.ai</a></p>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default SmsConsent;
